import { Formik } from 'formik';
import { useCallback } from 'react';
import { object, number } from 'yup';

import type { SurfaceFinish, MaterialRemovalType } from '@frontend/api';
import { RUNES_CALLOUT_TYPES, MATERIAL_REMOVAL_TYPES } from '@frontend/api';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  Checkbox,
  Flex,
  GDTSymbolText,
} from '@frontend/components';

import { SubFormProps } from '../types';
import { FormPreviewSection } from './shared/FormPreviewSection';
import { NumberValueField } from './shared/NumberInputValue';
import { SubFormWrapper } from './shared/SubFormWrapper';
import {
  createRequiredSelectField,
  createBooleanField,
} from './shared/validation';

type SurfaceFinishFormValues = {
  materialRemovalType: MaterialRemovalType;
  hasRaMean: boolean;
  raMean?: number;
};

const initialValues: SurfaceFinishFormValues = {
  materialRemovalType: MATERIAL_REMOVAL_TYPES.UNSPECIFIED,
  hasRaMean: false,
  raMean: undefined,
};

const MATERIAL_REMOVAL_TYPE_OPTIONS = Object.values(MATERIAL_REMOVAL_TYPES).map(
  (value) => ({
    value,
    label: value.charAt(0).toUpperCase() + value.slice(1),
  }),
);

const formatSurfaceFinishSummary = (
  values: SurfaceFinishFormValues,
): string => {
  let summary = '√';
  if (values.hasRaMean && values.raMean !== undefined) {
    summary = `${values.raMean}${summary}`;
  }
  return summary;
};

const validationSchema = object().shape({
  materialRemovalType: createRequiredSelectField('Material removal type'),
  hasRaMean: createBooleanField(),
  raMean: number()
    .typeError('Ra Mean must be a number')
    .min(0, 'Ra Mean must be non-negative')
    .when('hasRaMean', {
      is: true,
      then: (schema) => schema.required('Ra Mean is required when enabled'),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const SurfaceFinishForm = ({
  onSubmit,
  onCancel,
  renderSelectedType,
}: SubFormProps) => {
  const handleSubmit = useCallback(
    (values: SurfaceFinishFormValues) => {
      const surfaceFinish: SurfaceFinish = {
        calloutType: RUNES_CALLOUT_TYPES.SURFACE_FINISH,
        materialRemovalType: values.materialRemovalType,
        raMean:
          values.hasRaMean && values.raMean !== undefined
            ? values.raMean
            : null,
      };

      const summary = formatSurfaceFinishSummary(values);
      onSubmit(surfaceFinish, summary);
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => {
        const selectedOption = MATERIAL_REMOVAL_TYPE_OPTIONS.find(
          (option) => option.value === values.materialRemovalType,
        );

        return (
          <SubFormWrapper
            isValid={isValid}
            isSubmitting={isSubmitting}
            onCancel={onCancel}
            renderSelectedType={renderSelectedType}
            submitLabel="Add Surface Finish"
          >
            <FormPreviewSection>
              <GDTSymbolText>
                {formatSurfaceFinishSummary(values)}
              </GDTSymbolText>
            </FormPreviewSection>

            <Flex direction="column" gap={1} px={1} pb={1}>
              <FormControl>
                <FormLabel>Material Removal Type</FormLabel>
                <Select
                  name="materialRemovalType"
                  options={MATERIAL_REMOVAL_TYPE_OPTIONS}
                  value={selectedOption}
                  onChange={(
                    option: {
                      value: MaterialRemovalType;
                      label: string;
                    } | null,
                  ) => {
                    if (option?.value) {
                      setFieldValue('materialRemovalType', option.value);
                    }
                  }}
                  size="sm"
                  isClearable={false}
                />
                {errors.materialRemovalType && touched.materialRemovalType && (
                  <FormErrorMessage>
                    {errors.materialRemovalType}
                  </FormErrorMessage>
                )}
              </FormControl>

              <Checkbox
                name="hasRaMean"
                isChecked={values.hasRaMean}
                onChange={(e) => setFieldValue('hasRaMean', e.target.checked)}
                size="sm"
              >
                Include Ra Mean
              </Checkbox>
              {values.hasRaMean && (
                <FormControl>
                  <FormLabel>Ra Mean</FormLabel>
                  <NumberValueField
                    name="raMean"
                    value={values.raMean?.toString() ?? ''}
                    onChange={(value) => {
                      const numValue =
                        value === '' ? undefined : parseFloat(value);
                      setFieldValue(
                        'raMean',
                        isNaN(numValue as number) ? undefined : numValue,
                      );
                    }}
                    size="sm"
                    min={0}
                    allowDecimals={true}
                  />
                  {errors.raMean && touched.raMean && (
                    <FormErrorMessage>{errors.raMean}</FormErrorMessage>
                  )}
                </FormControl>
              )}
            </Flex>
          </SubFormWrapper>
        );
      }}
    </Formik>
  );
};
